/**
 * The module-level half of push: the native listeners, registered ONCE when
 * this feature is imported (the shell imports it eagerly, before the router
 * mounts), and the remembered rationale answer.
 *
 * ── WHY THE TAP LISTENER IS NOT INSIDE THE HOOK ────────────────────────────
 * A tap that cold-starts the app must be read before anything renders, and
 * whether it may navigate depends on whether a session is STORED at that
 * moment: a push tapped while signed out (the app was killed after a
 * sign-out) is dropped, never replayed after the next sign-in. So the
 * listener asks the `ApiClient` itself and puts the route into the
 * {@link openedInbox}; `usePushRouting` attaches once the router is ready.
 *
 * ── THE TOKEN IS NEVER LOGGED ──────────────────────────────────────────────
 * Nothing here prints a `PushState.token` (`native/push.ts`, `T-64`).
 */
import type { NotificationChannelKey } from "@repo/contract/notifications";
import {
  "push.channel_checkin" as channelCheckin,
  "push.channel_default" as channelDefault,
  "push.channel_reminder" as channelReminder,
  "push.channel_session" as channelSession,
  "push.channel_trainer" as channelTrainer,
} from "@repo/i18n";
import { api, onApiSessionEnded } from "../../data/api";
import {
  type AuthorizedFetch,
  disablePush,
  forgetPush,
  getLaunchUrl,
  isNative,
  onDeepLink,
  onPushOpened,
  onPushReceived,
  type PushMessage,
  type PushState,
  preferences,
  refreshPush,
} from "../../native";
import {
  CONSENT_KEY,
  type PushConsent,
  parseConsent,
  shouldAskRationale,
} from "./consent";
import { createInbox } from "./inbox";
import { dataFromLink } from "./link";

/** Tapped pushes' `data`, waiting for the router. */
export const openedInbox = createInbox<unknown>();

let foreground: ((message: PushMessage) => void) | null = null;

/**
 * The foreground-receipt handler. NOT buffered: a push that arrived while
 * nobody signed in was showing is not shown later. Returns the detach.
 */
export function onForegroundPush(
  handler: (message: PushMessage) => void
): () => void {
  foreground = handler;
  return () => {
    if (foreground === handler) {
      foreground = null;
    }
  };
}

export async function readConsent(): Promise<PushConsent | null> {
  try {
    return parseConsent(await preferences.get(CONSENT_KEY));
  } catch {
    return null;
  }
}

export async function writeConsent(consent: PushConsent): Promise<void> {
  await preferences.set(CONSENT_KEY, consent).catch(() => undefined);
}

async function clearConsent(): Promise<void> {
  await preferences.remove(CONSENT_KEY).catch(() => undefined);
}

const CHANNEL_NAMES: Record<NotificationChannelKey | "default", () => string> =
  {
    checkin: channelCheckin,
    default: channelDefault,
    reminder: channelReminder,
    session: channelSession,
    trainer: channelTrainer,
  };

/** The channel names in the live locale; the native default is English. */
export function channelName(kind: NotificationChannelKey | "default"): string {
  return CHANNEL_NAMES[kind]();
}

/**
 * The deliberate sign-out's push step. Runs BEFORE `ApiClient.signOut()`,
 * while the credential still authenticates the `DELETE /me/devices`
 * (`disablePush`, which also deletes the FCM token so the next person gets a
 * fresh one). If the server could not be told — offline, a 5xx — the local
 * record is dropped anyway (`forgetPush`): the next person to sign in here
 * must not inherit this person's registration. Never throws.
 */
export async function unregisterForSignOut(
  fetch: AuthorizedFetch
): Promise<void> {
  if (!isNative()) {
    return;
  }
  const state: PushState = await disablePush(fetch);
  if (state.status !== "off") {
    await forgetPush();
  }
  await clearConsent();
}

/**
 * The boot path after sign-in: re-register a person who opted in before
 * (`refreshPush` never prompts), then say whether to show the rationale.
 */
export async function bootPush(fetch: AuthorizedFetch): Promise<boolean> {
  const state = await refreshPush(fetch, channelName);
  return shouldAskRationale(state.status, await readConsent());
}

let started = false;

/**
 * The one door into the inbox: a tapped push, or a deep link that says the
 * same thing. Dropped when signed out — the app was killed after a sign-out
 * and the tap is not replayed after the next sign-in.
 */
function deliverWhenSignedIn(data: unknown): void {
  api
    .hasSession()
    .then((signedIn) => {
      if (signedIn) {
        openedInbox.deliver(data);
      }
    })
    .catch(() => undefined);
}

/** Registers the native listeners once. Web: nothing (every push call is a no-op there). */
export function startPushListeners(): void {
  if (started || !isNative()) {
    return;
  }
  started = true;

  onPushOpened((message) => {
    deliverWhenSignedIn(message.data);
  });

  // A deep link routes exactly like a tapped push: `dataFromLink` turns
  // `<scheme>://open?screen=…` into the same `data` dictionary, and anything
  // else into `{}`, which is Today (D96). Both halves are needed — the
  // listener fires for a warm start only, and `getLaunchUrl` is the cold one.
  // It is read ONCE here, at listener start, because it keeps returning the
  // same URL for the life of the process.
  onDeepLink((url) => {
    deliverWhenSignedIn(dataFromLink(url));
  });
  getLaunchUrl()
    .then((url) => {
      if (url !== null) {
        deliverWhenSignedIn(dataFromLink(url));
      }
    })
    .catch(() => undefined);

  onPushReceived((message) => {
    foreground?.(message);
  });

  // Every way a session ends — sign-out, a refresh the server refused, a
  // deleted account. The credential is already gone, so only local state is
  // dropped: the stored token (`forgetPush`, the session-EXPIRED door in
  // `native/push.ts`), a tap still waiting, and the rationale answer.
  onApiSessionEnded(() => {
    openedInbox.drop();
    forgetPush().catch(() => undefined);
    clearConsent().catch(() => undefined);
  });
}
